import { useEffect, useRef } from 'react';

import { logger } from '@/utils/logger';

interface UseDrawerSwipeOptions {
  onClose: () => void;
  enabled?: boolean;
  threshold?: number; // Minimum swipe distance in pixels
}

/**
 * Custom hook to enable right swipe gesture to close drawer on mobile devices.
 * Attach the returned ref to the drawer content element.
 */
export function useDrawerSwipe({ onClose, enabled = true, threshold = 100 }: UseDrawerSwipeOptions) {
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHorizontalSwipeRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      isHorizontalSwipeRef.current = false;

      logger.debug('[Drawer Swipe] Touch Start:', {
        x: touchStartX.current,
        y: touchStartY.current,
      });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) return;

      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;

      const deltaX = currentX - touchStartX.current;
      const deltaY = currentY - touchStartY.current;

      // Detect swipe direction early (within first 10px of movement)
      if (!isHorizontalSwipeRef.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        // Consider it horizontal if X movement is greater than Y movement
        isHorizontalSwipeRef.current = Math.abs(deltaX) > Math.abs(deltaY);

        logger.debug('[Drawer Swipe] Direction detected:', {
          isHorizontal: isHorizontalSwipeRef.current,
          deltaX,
          deltaY,
        });
      }

      // If horizontal swipe detected and swiping right, prevent default scroll
      if (isHorizontalSwipeRef.current && deltaX > 0) {
        const target = e.target as HTMLElement;

        // Don't interfere with focused inputs
        const isFocusedInput =
          (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') &&
          document.activeElement === target;

        if (isFocusedInput) {
          // Let the input handle the touch
          return;
        }

        // Check if target is a textarea - if so, only prevent if not scrolling
        const isTextarea = target.tagName === 'TEXTAREA';
        if (isTextarea) {
          // For textareas, only prevent if not scrolling vertically
          if (Math.abs(deltaY) < 5) {
            e.preventDefault();
          }
        } else {
          // For non-textareas, check scrollable parent
          const scrollableParent = target.closest('[class*="overflow-y-auto"]');

          // Only prevent default if not actively scrolling vertically
          if (!scrollableParent || scrollableParent.scrollTop === 0 || Math.abs(deltaY) < 5) {
            e.preventDefault();
          }
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartX.current === null || touchStartY.current === null) {
        logger.debug('[Drawer Swipe] Touch End - No valid start coordinates');
        return;
      }

      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;

      logger.debug('[Drawer Swipe] Touch End:', {
        deltaX,
        deltaY,
        threshold,
        isHorizontalSwipe: isHorizontalSwipeRef.current,
      });

      // Only close if:
      // 1. Horizontal swipe was detected
      // 2. Swiped right (deltaX > 0)
      // 3. Distance exceeds threshold
      if (isHorizontalSwipeRef.current && deltaX > threshold) {
        logger.debug('[Drawer Swipe] Closing drawer!');
        onClose();
      }

      // Reset
      touchStartX.current = null;
      touchStartY.current = null;
      isHorizontalSwipeRef.current = false;
    };

    // Use capture phase to intercept events before child elements
    container.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true, capture: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true, capture: true });

    logger.debug('[Drawer Swipe] Event listeners attached (capture phase)');

    return () => {
      container.removeEventListener('touchstart', handleTouchStart, { capture: true });
      container.removeEventListener('touchmove', handleTouchMove, { capture: true });
      container.removeEventListener('touchend', handleTouchEnd, { capture: true });
      container.removeEventListener('touchcancel', handleTouchEnd, { capture: true });
      logger.debug('[Drawer Swipe] Event listeners removed');
    };
  }, [onClose, enabled, threshold]);

  return containerRef;
}
